import { useState, useEffect } from 'react';
import fetchQuotaDetails, { QuotaDetailsResponse } from '@api/fetchers/common';
import { readCookie } from '@utils/cookies';

export interface UseQuotaDetailsReturn {
  showPrimeBanner: boolean;
  isLoading: boolean;
  data: QuotaDetailsResponse | null;
  isError: boolean;
}

const useQuotaDetails = (
  goalUID: string | undefined,
  isLoggedIn: boolean
): UseQuotaDetailsReturn => {
  const [data, setData] = useState<QuotaDetailsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const shouldFetch = !!goalUID && !!isLoggedIn && !!readCookie('accessToken');

  useEffect(() => {
    if (!shouldFetch) {
      setData(null);
      setIsLoading(false);
      setIsError(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setIsError(false);

    fetchQuotaDetails(goalUID!)
      .then((res) => {
        if (cancelled) return;
        if ((res as QuotaDetailsResponse & { error?: unknown })?.error)
          throw (res as QuotaDetailsResponse & { error: unknown }).error;
        setData(res);
      })
      .catch(() => {
        if (!cancelled) setIsError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [goalUID, shouldFetch]);

  const showPrimeBanner = data?.userPlatformPlan === 1;
  return {
    showPrimeBanner: !!showPrimeBanner,
    isLoading: shouldFetch && isLoading,
    data,
    isError
  };
};

export default useQuotaDetails;
